import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import { Helmet } from 'react-helmet'
import styled from '@emotion/styled'
import tw from 'twin.macro'

import { HTMLContent } from '../components/global/Content'

import Button from '../components/global/Button'
import Layout from '../components/Layout'
import HTML from '../components/global/HTML'
import Circle from '../components/global/Circle'
import Container from '../components/global/Container'
import Modal from '../components/global/Modal'
import PageHero from '../components/audience/PageHero'
import NewsletterForm from '../components/global/NewsletterForm'

export const AudienceTemplate = ({ data, content, contentComponent, slug }) => {

  const pageSlug = slug.replace(/\//g, '');
  const pageTitle = 'For ' + pageSlug.charAt(0).toUpperCase() + pageSlug.slice(1) + ' — Bindle';
  const PageContent = contentComponent

  Number.random = (minimum, maximum, precision) => {
    minimum = minimum === undefined ? 0 : minimum;
    maximum = maximum === undefined ? 9007199254740992 : maximum;
    precision = precision === undefined ? 0 : precision;
    const random = Math.random() * (maximum - minimum) + minimum;
    return random.toFixed(precision);
  }

  const circleSizes = data.circleProps.sizes.code.split(', ')

  const circlesCol = data.Circles.map((circle, index) => {
    return (
      <Circle 
        key={index} 
        size={circleSizes[index]}
        background={data.circleProps.color}
        color={data.circleProps.textColor}
        floating={false}
        opacity="0.8"
      >
        <HTML content={circle.text} />
      </Circle>
    );
  });

  const CirclesComponent = ({ className }) => {
    return (
      <div className={`${className} circles`}>
        {circlesCol}
      </div>
    )
  }
  CirclesComponent.propTypes = {
    className: PropTypes.string.isRequired,
  }

  const Circles = styled(CirclesComponent)`
    ${tw``}
    z-index: 10;
    float: right;
    padding: 60px;
    margin-top: -30%;
    width: ${data.circleProps.scale};
    .circle {
      margin: -40px;
    }
  `
  const ModalButton = () => (
    <Button>
      Get started now&nbsp;
      <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.51953 1.474L8.4546 8.40906L1.51953 15.3441" stroke="#00ADEE" strokeWidth="1.73377" strokeLinejoin="round"/></svg>
    </Button> 
  )

  const PageBodyComponent = ({ className, data, content, slug }) => {
    return (
      <section className={`${className} page-body ${slug}`}>
        <Container>
          <div className="content-inner">
            <h2>{data.title}</h2>
            <PageContent className="content" content={content} />
            <Modal trigger={<ModalButton />}>
              <NewsletterForm className="mailing-list-form" data={data.newsletter} />
            </Modal>
          </div>
          <Circles />
        </Container>
      </section>
    )
  }
  PageBodyComponent.propTypes = {
    className: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    content: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired
  }

  const PageBody = styled(PageBodyComponent)`
    ${tw``}
    background: url('../images/page-body-bg.jpg');
    background-size: cover;
    padding: 120px 0;
    box-sizing: border-box;
    position: relative;
    z-index: 10;
    @media (max-width: 1250px) {
      padding: 60px 0;
    }
    &::after {
      content: "";
      display: table;
      clear: both;
    }
    .content-inner {
      width: calc(95% - ${data.circleProps.scale});
      float: left;
      @media (max-width: 1250px) {
        float: none;
        width: 50%;
        padding-bottom: 100px;
      }
      @media (max-width: 639px) {
        width: 100%;
        padding-bottom: 0;
      }
    }
    h2 {
      width: 120%;
      font-family: 'Open Sans';
      font-weight: 400;
      font-size: 4rem;
      @media (max-width: 1023px) {
        font-size: 3rem;
        width: 100%;
      }
      @media (max-width: 639px) {
        font-size: 2rem;
      }
    }
    p {
      font-size: 1.2rem;
      margin-bottom: 2.5em;
      @media (max-width: 1023px) {
        font-size: 1rem;
      }
    }
    .circles {
      @media (max-width: 1250px) {
        width: 60%;
        position: absolute;
        top: 50px;
        right: 0;
        transform: scale(0.7);
        height: 1200px;
        .circle {
          font-size: 1.5rem;
        }
      }
      @media (max-width: 639px) {
        display: none;
      }
    }
    &.schools {
      .circle:nth-of-type(2),
      .circle:nth-of-type(5),
      .circle:nth-of-type(4) {
        @media (max-width: 1000px) {
          display: none;
        }
      }
    }
    &.businesses {
      .circle:nth-of-type(2),
      .circle:nth-of-type(4) {
        @media (max-width: 1000px) {
          display: none;
        }
      }
    }
  `

  return (
    <>
      <Helmet bodyAttributes={{ class: `${pageSlug}-page` }}>
        <title>{pageTitle}</title>
        <meta name="description" content={data.title} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:url" content="/" />
      </Helmet>
      <PageHero audience={pageSlug} data={data} />
      <PageBody data={data} content={content} slug={pageSlug} />
    </>
  )
}

AudienceTemplate.propTypes = {
  data: PropTypes.object.isRequired,
  slug: PropTypes.string.isRequired,
  content: PropTypes.string,
  contentComponent: PropTypes.func,
}

const Audience = ({ data }) => {
  const { frontmatter } = data.markdownRemark
  const { fields } = data.markdownRemark
  const { html } = data.markdownRemark
  const globalData = data.allMarkdownRemark.edges[0].node.frontmatter

  return (
    <Layout footerData={globalData}>
      <AudienceTemplate
        contentComponent={HTMLContent}
        content={html}
        data={frontmatter}
        slug={fields.slug}
      />
    </Layout>
  )
}

Audience.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      frontmatter: PropTypes.object,
      fields: PropTypes.object,
      html: PropTypes.string,
    }),
    allMarkdownRemark: PropTypes.shape({
      edges: PropTypes.array,
    }),
  }),
}

export default Audience

export const pageQuery = graphql`
  query AudiencePage($id: String!) {
    allMarkdownRemark(filter: {frontmatter: {path: {eq: "/global"}}}) {
      edges {
        node {
          frontmatter {
            footer {
              heading
              body
            }
            newsletter {
              heading
              text
              action
            }
          }
        }
      }
    }
    markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        heroTitle
        value
        heroFg {
          childImageSharp {
            fluid(maxWidth: 2048, quality: 70) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        heroBg {
          childImageSharp {
            fluid(maxWidth: 2048, quality: 70) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        title
        newsletter {
          heading
          text
          action
          group
        }
        circleProps {
          color
          textColor
          scale
          height
          sizes {
            code
          }
        }
        Circles {
          text
        }
      }
      fields {
        slug
      }
    }
  }
`
